import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BarChart3, Sparkles, Package, Award, Trophy, Sword, Shirt, Target, TrendingUp, Briefcase, Coffee, Wine, Music, Map, Key, Star, Crown, Headphones, Megaphone, Shield, Hammer, Camera, Cookie, Beer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  Sword, Shirt, Package, Sparkles, Target, TrendingUp, Briefcase, Trophy,
  Coffee, Wine, Music, Map, Key, Star, Crown, Headphones, Megaphone, Shield,
  Hammer, Award, Camera, Cookie, Beer,
};

export default function RiderProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/profiles/${id}/public`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => toast.error("Reiter konnte nicht geladen werden."))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-hammerl-paper text-hammerl-ink flex items-center justify-center">
        <div className="font-mono text-sm text-hammerl-pencil">Lade Reiter-Akte…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-hammerl-paper text-hammerl-ink flex items-center justify-center p-6">
        <div className="text-center space-y-4 max-w-md">
          <div className="font-bold text-2xl">Akte nicht auffindbar.</div>
          <p className="font-handwriting text-lg text-hammerl-pencil">Hammerl hat die Datei wohl im Archiv vergraben.</p>
          <Button onClick={() => navigate("/scoreboard")} className="rounded-none bg-hammerl-corpblue text-white font-bold" data-testid="rider-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zu den Charts
          </Button>
        </div>
      </div>
    );
  }

  const xpForNext = data.level * (data.level + 1) * 50;
  const xpPercent = Math.min(100, Math.round((data.xp / xpForNext) * 100));
  const totalBonus =
    (data.skills || []).reduce((s, x) => s + (x.dice_bonus || 0), 0) +
    (data.inventory || []).reduce((s, x) => s + (x.bonus || 0), 0);

  return (
    <div className="min-h-screen bg-hammerl-paper text-hammerl-ink">
      <header className="border-b-2 border-hammerl-ink bg-hammerl-card px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img src="/brand/hammerl-logo.jpg" alt="Logo" className="w-9 h-9 object-cover border border-hammerl-ink" />
          <div>
            <div className="font-bold text-xs sm:text-sm tracking-tight">Hobby Horsing Hammerl</div>
            <div className="text-[10px] sm:text-xs text-hammerl-pencil font-mono">Reiter-Akte (öffentlich)</div>
          </div>
        </div>
        <Button
          onClick={() => navigate("/scoreboard")}
          variant="ghost"
          className="rounded-none hover:bg-hammerl-highlight hover:text-hammerl-paper font-mono text-xs"
          data-testid="rider-back-btn"
        >
          <BarChart3 className="w-4 h-4 mr-1" /> Charts
        </Button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="rider-profile">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-hammerl-card border-2 border-hammerl-ink shadow-brutal p-5"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Reiter-Performance</div>
          <h1 className="font-bold text-3xl mt-1 break-words" data-testid="rider-name">{data.name}</h1>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Level" value={`LVL ${data.level}`} />
            <Stat label="XP" value={`${data.xp} / ${xpForNext}`} />
            <Stat label="Streak" value={`${data.streak_current} Tage`} sub={`Best: ${data.streak_max}`} />
            <Stat label="Aktionen" value={data.actions_count} />
          </div>
          <Progress value={xpPercent} className="h-3 rounded-none bg-hammerl-sidebar border border-hammerl-ink [&>div]:bg-hammerl-corpblue mt-4" />
          {totalBonus > 0 && (
            <div className="mt-3 inline-flex items-center px-2 py-0.5 border border-hammerl-ink bg-hammerl-highlight text-hammerl-paper font-mono text-xs font-bold">
              Gesamt-Würfelbonus: +{totalBonus}%
            </div>
          )}
        </motion.div>

        {/* Skills */}
        <Section title="Skill-Matrix" count={data.skills?.length || 0} accent="bg-hammerl-highlight text-hammerl-paper">
          {(data.skills || []).map((s, i) => (
            <button
              key={s.name || `skill-${i}`}
              type="button"
              onClick={() => setDetail({ kind: "skill", name: s.name, description: s.description, bonus: s.dice_bonus || 0, icon: "Sparkles" })}
              className="w-full text-left p-2 hover:bg-hammerl-sidebar focus:bg-hammerl-sidebar focus:outline-none transition-colors border-b border-hammerl-lines last:border-b-0"
              data-testid={`rider-skill-${i}`}
            >
              <div className="font-bold text-sm flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <Sparkles className="w-3.5 h-3.5 text-hammerl-corpblue shrink-0" />
                  <span className="truncate">{s.name}</span>
                </span>
                {(s.dice_bonus || 0) > 0 && (
                  <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-mixed text-hammerl-paper font-mono text-[10px] font-bold">+{s.dice_bonus}%</span>
                )}
              </div>
              {s.description && <div className="text-xs text-hammerl-pencil ml-5 truncate">{s.description}</div>}
            </button>
          ))}
        </Section>

        {/* Inventory */}
        <Section title="Ausrüstung" count={data.inventory?.length || 0} accent="bg-hammerl-mixed text-hammerl-paper">
          {(data.inventory || []).map((it, i) => {
            const Icon = ICON_MAP[it.icon] || Package;
            return (
              <button
                key={`${it.name}-${i}`}
                type="button"
                onClick={() => setDetail({ kind: "item", name: it.name, description: it.description, bonus: it.bonus || 0, icon: it.icon || "Package" })}
                className="w-full text-left flex items-start gap-3 p-2 hover:bg-hammerl-sidebar focus:bg-hammerl-sidebar transition-colors"
                data-testid={`rider-inventory-${i}`}
              >
                <div className="w-9 h-9 bg-hammerl-sidebar border border-hammerl-ink flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm flex items-center justify-between gap-2">
                    <span className="truncate">{it.name}</span>
                    {(it.bonus || 0) > 0 && (
                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-highlight text-hammerl-paper font-mono text-[10px] font-bold">+{it.bonus}%</span>
                    )}
                  </div>
                  {it.description && <div className="text-xs text-hammerl-pencil truncate">{it.description}</div>}
                </div>
              </button>
            );
          })}
        </Section>

        {/* Achievements */}
        <Section title="Auszeichnungen" count={data.achievements?.length || 0} accent="bg-hammerl-corpblue text-white">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 p-2">
            {(data.achievements || []).slice().reverse().slice(0, 18).map((a) => {
              const Icon = ICON_MAP[a.icon] || Award;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setDetail({ kind: "skill", name: a.name, description: a.description, bonus: 0, icon: a.icon || "Award" })}
                  className="aspect-square bg-hammerl-card border-2 border-hammerl-ink flex items-center justify-center hover:bg-hammerl-highlight transition-colors shadow-brutal-sm"
                  data-testid={`rider-ach-${a.id}`}
                  aria-label={a.name}
                >
                  <Icon className="w-4 h-4 text-hammerl-corpblue" />
                </button>
              );
            })}
          </div>
        </Section>

        <div className="text-[10px] font-mono uppercase tracking-widest text-hammerl-pencil text-center pt-2">
          Öffentliche Akte · ohne private Daten · Hammerl-Audit-Stempel
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-md rounded-none border-2 border-hammerl-ink shadow-brutal bg-hammerl-card p-5" data-testid="rider-detail-dialog">
          {detail && (
            <>
              <DialogTitle className="font-bold text-lg break-words">{detail.name}</DialogTitle>
              {detail.bonus > 0 && (
                <span className="mt-1 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-highlight text-hammerl-paper font-mono text-[10px] font-bold w-fit">
                  +{detail.bonus}% Wurf-Chance
                </span>
              )}
              {detail.description ? (
                <DialogDescription className="text-sm text-hammerl-ink leading-relaxed break-words mt-2">
                  {detail.description}
                </DialogDescription>
              ) : (
                <DialogDescription className="text-sm text-hammerl-pencil italic mt-2">Keine Beschreibung hinterlegt.</DialogDescription>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-hammerl-paper border border-hammerl-lines px-3 py-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">{label}</div>
      <div className="font-mono font-bold text-base">{value}</div>
      {sub && <div className="font-mono text-[10px] text-hammerl-pencil">{sub}</div>}
    </div>
  );
}

function Section({ title, count, accent, children }) {
  return (
    <div className="bg-hammerl-card border-2 border-hammerl-ink">
      <div className={`border-b-2 border-hammerl-ink px-4 py-2 ${accent} font-mono text-xs uppercase tracking-widest flex items-center justify-between`}>
        <span>{title}</span>
        <span>{count}</span>
      </div>
      <div className="p-2">
        {children}
      </div>
    </div>
  );
}
